'use client';

import { Icon, Logo } from '../../components';
import { Paper } from '../../layout';
import type { LinkItem } from '../../types';

export interface EmailVerificationPendingProps {
  title: React.ReactNode;
  /** Email address where verification was sent */
  email: string;
  /** Main message, e.g. "Account created. Please check your email." */
  message?: string;
  /** Secondary message with email info, e.g. "We sent an email to" */
  emailSentToText?: string;
  /** Tip about spam folder */
  spamTip?: string;
  /** Info about link expiration */
  expirationInfo?: string;
  /** Back to sign in link */
  backToSignInLink?: LinkItem;
  /** Show links */
  links?: boolean;
}

export function EmailVerificationPending(props: EmailVerificationPendingProps) {
  const {
    title,
    email,
    message = 'Your account has been created. Please check your email and click the verification link.',
    emailSentToText = 'We sent an email to',
    spamTip = "Check your spam or promotions folder if you don't see it.",
    expirationInfo = 'The verification link is valid for 24 hours.',
    backToSignInLink,
    links = false
  } = props;

  return (
    <div className='flex flex-col items-center gap-xl w-full max-w-md px-md sm:px-0'>
      <Logo className='h-10' />
      <Paper className='flex flex-col items-center gap-sm shadow-lg border border-border-base rounded-xl w-full p-lg sm:p-xl'>
        <Paper.Title>{title}</Paper.Title>
        <Paper.Content className='w-full flex flex-col items-center'>
          <div className='text-center py-sm'>
            <div className='mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-primary-base/10 mb-md'>
              <Icon name='envelope' size='6xl' className='text-primary-base' />
            </div>
            <p className='text-foreground mb-md'>{message}</p>
            <p className='text-foreground-muted text-sm mb-sm'>
              {emailSentToText} <strong className='text-foreground'>{email}</strong>
            </p>
            <p className='text-foreground-muted text-sm mb-sm'>{spamTip}</p>
            <p className='text-foreground-muted text-sm mb-md'>{expirationInfo}</p>
            {links && backToSignInLink && (
              <a
                href={backToSignInLink.href}
                className='text-primary-base hover:text-primary-hover flex items-center justify-center gap-xs mt-md'
              >
                <Icon name='chevron-down' rotate={90} className='text-primary-base' />
                {backToSignInLink.label}
              </a>
            )}
          </div>
        </Paper.Content>
      </Paper>
    </div>
  );
}
